// sim/ecosystem.ts — the ECOSYSTEM tick: population dynamics + ecological pressure.
//
// This system keeps the wildlife population alive and bounded over time. On a
// fixed cadence it computes a CARRYING CAPACITY and tops the population back up
// toward it, a few spawns at a time, via a deterministic species cycle.
//
//   carryingCapacity = min(maxWildlife, baseCapacity + capacityPerAnchor * anchors)
//
// PRESSURE SCALES WITH COLONY EXPANSION: more colony anchors ⇒ a higher ceiling,
// so a sprawling colony map sustains more predators bearing down on it. The
// `baseCapacity` floor keeps wildlife alive with ZERO colonies, so the ecosystem
// is INDEPENDENT of colonies (it doesn't need them to exist).
//
// DETERMINISM: pure integer state (exact FNV hashing), fixed iteration, RNG only
// from the (wildlife) stream passed in. Allocation-free per tick.

import { NIL } from '../types.ts';
import { SPECIES } from '../types.ts';
import type { System, Tick, Species, EcosystemConfig } from '../types.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { WildlifeSystem, ColonyPresence } from './wildlife.ts';
import { wildlifeTargetCount } from './enemyScaling.ts';

/**
 * Deterministic species spawn cycle for maintenance + seeding. Rat-heavy (the
 * colony raider) with steady Birds/Spiders, the occasional Frog, plus the
 * scavenger Beetle and the territorial Dragonfly. Advanced by a single cursor so
 * the spawn mix is identical per seed.
 */
const SPAWN_CYCLE: readonly Species[] = [
  SPECIES.Rat,
  SPECIES.Bird,
  SPECIES.Spider,
  SPECIES.Beetle,
  SPECIES.Rat,
  SPECIES.Frog,
  SPECIES.Bird,
  SPECIES.Dragonfly,
];

export class EcosystemSystem implements System {
  readonly name = 'ecosystem';

  private readonly wildlife: WildlifeSystem;
  private readonly grid: WorldGrid;
  private readonly rng: Rng;
  private readonly config: EcosystemConfig;
  private readonly presence: ColonyPresence | undefined;
  /**
   * Optional ecosystem-event modifier (read-only here). When present, the
   * carrying capacity is scaled by `wildlifeCapacityPct/100` (before the
   * maxWildlife clamp); when absent it is neutral (100), so an events-off world
   * is byte-identical. The event system owns the struct; we only read it.
   */
  private readonly mods: { wildlifeCapacityPct: number } | undefined;

  /**
   * OPTIONAL player-power source (late-bound via `attachPowerSource`). When wired,
   * the carrying capacity is POWER-TARGETED — the wildlife head-count tracks a band
   * around the player colony's strength (enemyScaling.WILDLIFE_BAND) instead of the
   * literal base+anchor formula — so the ambient threat stays relevant as the player
   * grows. Absent ⇒ the legacy base+anchor capacity, byte-identical to before.
   */
  private powerSource: (() => number) | null = null;

  /** Cursor into SPAWN_CYCLE (deterministic species rotation). */
  private cursor = 0;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  maintenancePasses = 0;
  spawnedByPassTotal = 0;

  constructor(
    wildlife: WildlifeSystem,
    grid: WorldGrid,
    rng: Rng,
    config: EcosystemConfig,
    presence?: ColonyPresence,
    mods?: { wildlifeCapacityPct: number },
  ) {
    this.wildlife = wildlife;
    this.grid = grid;
    this.rng = rng;
    this.config = config;
    this.presence = presence;
    this.mods = mods;
  }

  /** Current colony anchor count (0 if no colonies). */
  private anchors(): number {
    return this.presence?.anchorCount() ?? 0;
  }

  /**
   * Late-bind the player-power source (mirrors ApexSystem.attachPowerSource and the
   * other optional ecosystem seams). Once wired, `carryingCapacity` becomes power-
   * targeted; not wiring it leaves the legacy base+anchor capacity untouched.
   */
  attachPowerSource(source: () => number): void {
    this.powerSource = source;
  }

  /**
   * The wildlife population ceiling. With a power source wired it is POWER-TARGETED:
   * the head-count bands to enemyScaling.WILDLIFE_BAND × the player colony's power
   * (with per-pass variance from the wildlife RNG), floored so the world is never
   * empty and capped at maxWildlife so a swarm is never impossible. Without one it
   * falls back to the legacy `baseCapacity + capacityPerAnchor × anchors`. The event
   * modifier (wildlifeCapacityPct) and the hard maxWildlife clamp apply to BOTH paths.
   */
  carryingCapacity(): number {
    const cfg = this.config;
    let want: number;
    if (this.powerSource !== null) {
      // POWER-TARGETED: the band already captures colony-expansion pressure (a bigger,
      // stronger colony reads as higher power), so the anchor term is subsumed here.
      want = wildlifeTargetCount(this.powerSource(), cfg.maxWildlife, this.rng);
    } else {
      want = cfg.baseCapacity + cfg.capacityPerAnchor * this.anchors();
    }
    // Event-scaled ceiling: wildlifeCapacityPct/100, applied BEFORE the hard
    // maxWildlife clamp (neutral 100 ⇒ unchanged).
    const scale = this.mods ? this.mods.wildlifeCapacityPct : 100;
    want = Math.floor((want * scale) / 100);
    return want < cfg.maxWildlife ? want : cfg.maxWildlife;
  }

  /**
   * Seed the initial wildlife population (config.initialCounts per species) at
   * deterministic passable tiles. Call ONCE during world build, AFTER walls/food/
   * colonies are placed so spawns land on open ground.
   */
  seedInitial(): void {
    const counts = this.config.initialCounts;
    for (let sp = 0; sp < counts.length; sp++) {
      const n = counts[sp] as number;
      for (let k = 0; k < n; k++) {
        const tile = this.pickPassable();
        if (tile === NIL) break;
        this.wildlife.spawnWildlife(sp as Species, tile % this.grid.width, (tile / this.grid.width) | 0);
      }
    }
  }

  // --- Per-tick: maintain the population toward carrying capacity -------------

  update(tick: Tick): void {
    if (tick % this.config.spawnInterval !== 0) return;
    this.maintenancePasses++;

    const target = this.carryingCapacity();
    const deficit = target - this.wildlife.population();
    if (deficit <= 0) return;

    let n = this.config.spawnsPerPass;
    if (deficit < n) n = deficit;
    for (let k = 0; k < n; k++) {
      const sp = SPAWN_CYCLE[this.cursor % SPAWN_CYCLE.length] as Species;
      this.cursor = (this.cursor + 1) % SPAWN_CYCLE.length;
      const tile = this.pickPassable();
      if (tile === NIL) break;
      this.wildlife.spawnWildlife(sp, tile % this.grid.width, (tile / this.grid.width) | 0);
      this.spawnedByPassTotal++;
    }
  }

  /**
   * Find a passable tile (deterministic, wildlife-RNG driven). Returns a linear
   * tile index, or NIL if no passable tile turned up within the bounded budget.
   * Bounded retries keep it allocation-free and deterministic.
   */
  private pickPassable(): number {
    const grid = this.grid;
    let tries = 0;
    let x = 0;
    let y = 0;
    do {
      x = this.rng.int(grid.width);
      y = this.rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (!grid.isPassable(x, y)) return NIL;
    return grid.index(x, y);
  }
}
